import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Aperture, CalendarX2, Loader2, LogOut, Plus, Trash2, Upload } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { PricingEditor } from "../components/PricingEditor";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Calendar } from "../components/ui/calendar";

const STATUSES = ["nuova", "contattata", "confermata", "completata"];
const STATUS_COLORS = {
  nuova: "text-[#D6705A]",
  contattata: "text-amber-300",
  confermata: "text-emerald-300",
  completata: "text-[#A0A0A0]",
};
const CATEGORIES = ["Case Vacanze", "B&B", "Hotel & Resort", "Agenzie Immobiliari", "Property Manager", "Altro"];

const toDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const availCalendarClassNames = {
  caption_label: "text-sm font-medium text-[#F9F8F6] capitalize",
  head_cell: "text-[#666] rounded-md w-9 font-normal text-[0.75rem]",
  day: "h-9 w-9 p-0 font-normal text-[#C9C7C3] hover:bg-white/10 rounded-full transition-colors aria-selected:opacity-100",
  day_today: "text-[#D6705A] font-semibold",
  day_outside: "day-outside text-[#555] opacity-50",
  day_disabled: "text-[#555] opacity-40 hover:bg-transparent",
  nav_button: "h-7 w-7 bg-transparent p-0 text-[#A0A0A0] opacity-70 hover:opacity-100 border-white/10",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState({ title: "", category: "", image_url: "", description: "" });
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/portfolio/upload", fd);
      setNewItem((n) => ({ ...n, image_url: data.image_url }));
      toast.success("Immagine caricata.");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const load = useCallback(async () => {
    try {
      const [b, p, a] = await Promise.all([
        api.get("/bookings"),
        api.get("/portfolio"),
        api.get("/availability"),
      ]);
      setBookings(b.data);
      setPortfolio(p.data);
      setBlocked(a.data?.blocked || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
    else if (user) load();
  }, [user, navigate, load]);

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0D0F12]" data-testid="admin-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#D6705A]" />
      </div>
    );
  }

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Stato aggiornato.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeBooking = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      setBookings((bs) => bs.filter((b) => b.id !== id));
      toast.success("Richiesta eliminata.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleDay = async (day) => {
    if (!day) return;
    const key = format(day, "yyyy-MM-dd");
    try {
      if (blocked.includes(key)) {
        await api.delete(`/availability/${key}`);
        setBlocked((b) => b.filter((x) => x !== key));
        toast.success(`${format(day, "d MMMM", { locale: it })} di nuovo disponibile.`);
      } else {
        await api.post("/availability", { date: key });
        setBlocked((b) => [...b, key].sort());
        toast.success(`${format(day, "d MMMM", { locale: it })} bloccato. I clienti non potranno sceglierlo.`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!newItem.category) {
      toast.error("Seleziona una categoria.");
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post("/portfolio", newItem);
      setPortfolio((p) => [...p, data]);
      setNewItem({ title: "", category: "", image_url: "", description: "" });
      toast.success("Elemento aggiunto al portfolio.");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setAdding(false);
    }
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/portfolio/${id}`);
      setPortfolio((p) => p.filter((x) => x.id !== id));
      toast.success("Elemento eliminato.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const counts = {
    totale: bookings.length,
    nuove: bookings.filter((b) => b.status === "nuova").length,
    confermate: bookings.filter((b) => b.status === "confermata").length,
  };

  return (
    <div className="min-h-screen bg-[#0D0F12] text-[#F9F8F6]" data-testid="admin-dashboard">
      <header className="border-b border-white/5 px-6 py-4 lg:px-12">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Aperture className="h-5 w-5 text-[#D6705A]" strokeWidth={1.5} />
            <span className="font-display text-lg">Dashboard</span>
            <span className="ml-3 font-code text-[10px] uppercase tracking-[0.25em] text-[#666]" data-testid="admin-user-email">{user.email}</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" className="font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0] hover:text-[#F9F8F6]" data-testid="admin-view-site">
              Vedi il sito
            </Link>
            <button
              onClick={async () => { await logout(); navigate("/admin/login"); }}
              className="flex items-center gap-2 font-code text-[10px] uppercase tracking-[0.25em] text-[#D6705A] hover:text-[#F9F8F6]"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-3.5 w-3.5" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-12 lg:px-12">
        <div className="grid grid-cols-3 gap-4">
          {[["Richieste totali", counts.totale, "stat-total"], ["Da leggere", counts.nuove, "stat-new"], ["Confermate", counts.confermate, "stat-confirmed"]].map(([label, val, tid]) => (
            <div key={tid} className="border border-white/10 p-6" data-testid={tid}>
              <p className="font-code text-[10px] uppercase tracking-[0.25em] text-[#666]">{label}</p>
              <p className="mt-2 font-display text-4xl font-light">{val}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="bookings" className="mt-12">
          <TabsList className="bg-transparent border border-white/10 rounded-none">
            <TabsTrigger value="bookings" className="rounded-none font-code text-[10px] uppercase tracking-[0.2em] data-[state=active]:bg-[#D6705A] data-[state=active]:text-[#0D0F12]" data-testid="tab-bookings">
              Richieste
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="rounded-none font-code text-[10px] uppercase tracking-[0.2em] data-[state=active]:bg-[#D6705A] data-[state=active]:text-[#0D0F12]" data-testid="tab-portfolio">
              Portfolio
            </TabsTrigger>
            <TabsTrigger value="prezzi" className="rounded-none font-code text-[10px] uppercase tracking-[0.2em] data-[state=active]:bg-[#D6705A] data-[state=active]:text-[#0D0F12]" data-testid="tab-pricing">
              Prezzi
            </TabsTrigger>
            <TabsTrigger value="disponibilita" className="rounded-none font-code text-[10px] uppercase tracking-[0.2em] data-[state=active]:bg-[#D6705A] data-[state=active]:text-[#0D0F12]" data-testid="tab-availability">
              Disponibilità
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-8">
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-[#D6705A]" />
            ) : bookings.length === 0 ? (
              <p className="border border-white/10 p-12 text-center text-sm text-[#666]" data-testid="bookings-empty">
                Nessuna richiesta ancora. Le nuove prenotazioni appariranno qui.
              </p>
            ) : (
              <div className="overflow-x-auto border border-white/10">
                <Table data-testid="bookings-table">
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      {["Data", "Cliente", "Servizio", "Località", "Stato", ""].map((h) => (
                        <TableHead key={h} className="font-code text-[10px] uppercase tracking-[0.2em] text-[#666]">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.map((b) => (
                      <TableRow key={b.id} className="border-white/5 hover:bg-white/[0.02]" data-testid={`booking-row-${b.id}`}>
                        <TableCell className="font-code text-xs text-[#A0A0A0]">
                          {new Date(b.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{b.name}</p>
                          <p className="text-xs text-[#666]">{b.email}{b.phone ? ` · ${b.phone}` : ""}</p>
                          {b.message && <p className="mt-1 max-w-xs truncate text-xs text-[#555]" title={b.message}>{b.message}</p>}
                        </TableCell>
                        <TableCell className="text-sm text-[#A0A0A0]">
                          {b.service}
                          {b.package_name && (
                            <span className="block text-xs text-[#666]">
                              {b.package_name}
                              {b.photo_pack ? ` · ${b.photo_pack}` : ""}
                              {b.extras?.length ? ` · ${b.extras.join(", ")}` : ""}
                              {b.estimate != null ? ` — stima €${b.estimate}` : ""}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-[#A0A0A0]">
                          {b.location}
                          {b.preferred_date && <span className="block text-xs text-[#666]">Pref: {b.preferred_date}</span>}
                          {b.travel && (
                            <span className="block text-xs text-[#666]" data-testid={`booking-travel-${b.id}`}>
                              {b.travel.distance_km != null ? `Distanza: ${b.travel.distance_km} km (A/R ${b.travel.round_trip_km} km) · ${b.travel.band} · ` : ""}
                              Trasferta: {b.travel.status === "included" ? "inclusa" : b.travel.status === "charged" ? `€${b.travel.cost} + IVA` : b.travel.status === "custom_quote" ? "preventivo personalizzato" : "da verificare"}
                            </span>
                          )}
                          {b.total_with_travel != null && (
                            <span className="block text-xs font-medium text-[#D6705A]" data-testid={`booking-total-${b.id}`}>
                              Totale indicativo: €{b.total_with_travel} + IVA
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
                            <SelectTrigger className={`h-9 w-[140px] rounded-none border-white/10 bg-transparent font-code text-[10px] uppercase tracking-[0.15em] ${STATUS_COLORS[b.status]}`} data-testid={`booking-status-${b.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#16191D] border-white/10 text-[#F9F8F6]">
                              {STATUSES.map((s) => (
                                <SelectItem key={s} value={s} className="font-code text-[10px] uppercase tracking-[0.15em]">{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <button onClick={() => removeBooking(b.id)} className="text-[#555] transition-colors hover:text-red-400" data-testid={`booking-delete-${b.id}`} aria-label="Elimina">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="portfolio" className="mt-8">
            <form onSubmit={addItem} className="grid gap-4 border border-white/10 p-6 md:grid-cols-6" data-testid="portfolio-add-form">
              <Input placeholder="Titolo *" required value={newItem.title} onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                className="bg-[#0D0F12] border-white/10 rounded-none h-11" data-testid="portfolio-title-input" />
              <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                <SelectTrigger className="h-11 rounded-none border-white/10 bg-[#0D0F12]" data-testid="portfolio-category-select">
                  <SelectValue placeholder="Categoria *" />
                </SelectTrigger>
                <SelectContent className="bg-[#16191D] border-white/10 text-[#F9F8F6]">
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <label
                className={`flex h-11 cursor-pointer items-center justify-center gap-2 border border-dashed font-code text-[10px] uppercase tracking-[0.15em] transition-colors ${
                  newItem.image_url.startsWith("/api/files")
                    ? "border-[#D6705A] text-[#D6705A]"
                    : "border-white/20 text-[#A0A0A0] hover:border-[#D6705A] hover:text-[#D6705A]"
                }`}
                data-testid="portfolio-upload-label"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Caricamento…" : newItem.image_url.startsWith("/api/files") ? "Foto caricata" : "Carica foto"}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="portfolio-file-input" />
              </label>
              <Input placeholder="oppure URL immagine *" required type="text" value={newItem.image_url} onChange={(e) => setNewItem({ ...newItem, image_url: e.target.value })}
                className="bg-[#0D0F12] border-white/10 rounded-none h-11" data-testid="portfolio-image-input" />
              <Input placeholder="Descrizione" value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                className="bg-[#0D0F12] border-white/10 rounded-none h-11" data-testid="portfolio-description-input" />
              <button type="submit" disabled={adding}
                className="flex items-center justify-center gap-2 bg-[#D6705A] px-4 font-code text-[10px] uppercase tracking-[0.2em] text-[#0D0F12] transition-colors hover:bg-[#F9F8F6] disabled:opacity-60"
                data-testid="portfolio-add-button">
                {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Aggiungi
              </button>
            </form>

            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="portfolio-admin-grid">
              {portfolio.map((item) => (
                <div key={item.id} className="group relative overflow-hidden border border-white/10" data-testid={`portfolio-admin-item-${item.id}`}>
                  <img src={item.image_url} alt={item.title} className="aspect-[4/3] w-full object-cover" />
                  <div className="flex items-center justify-between p-4">
                    <div>
                      <p className="font-code text-[9px] uppercase tracking-[0.2em] text-[#D6705A]">{item.category}</p>
                      <p className="mt-1 text-sm">{item.title}</p>
                    </div>
                    <button onClick={() => removeItem(item.id)} className="text-[#555] transition-colors hover:text-red-400" data-testid={`portfolio-delete-${item.id}`} aria-label="Elimina">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="prezzi" className="mt-8">
            <PricingEditor />
          </TabsContent>

          <TabsContent value="disponibilita" className="mt-8">
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="border border-white/10 p-6">
                <div className="flex items-center gap-3">
                  <CalendarX2 className="h-4 w-4 text-[#D6705A]" />
                  <p className="font-code text-[10px] uppercase tracking-[0.25em] text-[#666]">
                    Clicca un giorno per bloccarlo o riaprirlo
                  </p>
                </div>
                <div className="mt-6 flex justify-center" data-testid="availability-calendar">
                  <Calendar
                    mode="single"
                    locale={it}
                    onDayClick={toggleDay}
                    disabled={{ before: new Date() }}
                    modifiers={{ blocked: blocked.map(toDate) }}
                    modifiersClassNames={{ blocked: "bg-[#D6705A]/15 text-[#D6705A] line-through hover:bg-[#D6705A]/25" }}
                    classNames={availCalendarClassNames}
                  />
                </div>
                <p className="mt-4 text-xs leading-relaxed text-[#666]">
                  I giorni bloccati appaiono barrati anche ai clienti nel form di prenotazione. La conferma dello shooting resta sempre manuale via email.
                </p>
              </div>
              <div className="border border-white/10 p-6">
                <p className="font-code text-[10px] uppercase tracking-[0.25em] text-[#666]">Prossimi giorni bloccati</p>
                {blocked.length === 0 ? (
                  <p className="mt-6 text-sm text-[#666]" data-testid="availability-empty">
                    Nessun giorno bloccato: tutte le date future sono selezionabili dai clienti.
                  </p>
                ) : (
                  <ul className="mt-6 space-y-2" data-testid="availability-list">
                    {blocked.map((d) => (
                      <li key={d} className="flex items-center justify-between border border-white/5 px-4 py-3" data-testid={`availability-item-${d}`}>
                        <span className="font-code text-sm capitalize text-[#C9C7C3]">
                          {format(toDate(d), "EEEE d MMMM yyyy", { locale: it })}
                        </span>
                        <button
                          onClick={() => toggleDay(toDate(d))}
                          className="text-[#555] transition-colors hover:text-red-400"
                          data-testid={`availability-remove-${d}`}
                          aria-label={`Sblocca ${d}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
